import { match } from "ts-pattern";
import Typography from "@mui/material/Typography";
import type { FilterChip } from "../../types";
import {
  defaultTargetOrganizationsSlice,
  type TargetOrganizationsSlice,
} from "./slice";
import { getActiveIds } from "./utils";

type LabelState = "loading" | "single" | "multiple";

function getLabelState(activeIds: string[], cachedIds: string[]): LabelState {
  // If we have IDs but cache is empty or incomplete, we're loading
  const isLoading = activeIds.length > 0 && cachedIds.length < activeIds.length;

  if (isLoading) return "loading";
  if (activeIds.length === 1) return "single";
  return "multiple";
}

export function getChips(
  slice: TargetOrganizationsSlice,
  setSlice: (slice: TargetOrganizationsSlice) => void
): FilterChip[] {
  const activeIds = getActiveIds(slice);
  if (activeIds.length === 0) return [];

  const cachedById = new Map(slice.companiesCache.map((c) => [c.hgId, c]));
  const names = activeIds
    .map((id) => cachedById.get(id)?.name)
    .filter((name): name is string => !!name);

  const labelState = getLabelState(activeIds, names);

  const label = match(labelState)
    .with("loading", () => "Loading...")
    .with("single", () => names[0] || "1 selected")
    .with("multiple", () => `${activeIds.length} selected`)
    .exhaustive();

  const includeCorporateFamily =
    slice.hgIdsIncludingCorporateRelatives.length > 0;

  // Only show tooltip when data is loaded
  const tooltipContent =
    labelState !== "loading" && names.length > 0 ? (
      <>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 500, color: "#424242", mb: 0.5 }}
        >
          Companies
        </Typography>
        {includeCorporateFamily && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Includes corporate family (parent + subsidiaries)
          </Typography>
        )}
        {names.map((name, idx) => (
          <Typography
            key={idx}
            variant="body2"
            sx={{ fontSize: 12, color: "#666", py: 0.25 }}
          >
            {name}
          </Typography>
        ))}
      </>
    ) : undefined;

  return [
    {
      id: "targetOrganizations",
      label: `Companies: ${label}`,
      filterKey: "targetOrganizations" as const,
      onRemove: () => setSlice(defaultTargetOrganizationsSlice),
      tooltipContent,
    },
  ];
}

export function getCount(slice: TargetOrganizationsSlice): number {
  return getActiveIds(slice).length;
}
